/* =============================================================================
 * CoachReportModal (APS-260)
 * -----------------------------------------------------------------------------
 * A family tells us a coach's details are out of date. The report goes to the
 * coach review queue. Staff check it before anything changes.
 *
 * Opened three ways:
 *   - "Report a change" on a coach in the school pop-up: pass `coach`.
 *   - "Report a missing coach" under the staff list: pass initialKind="missing"
 *     and the staff as `coaches`, so the family can still switch to a coach.
 *   - "Report a change" on a school card: pass `loadCoaches`, and the modal
 *     loads the staff so the family can pick the coach.
 *
 * Contact data: the current email and phone show only when the parent says
 * the viewer can see them (`contactVisible`) and the value is really there.
 * The inputs always start empty, so a free family never sees a hidden value.
 * ========================================================================== */
import React, { useEffect, useRef, useState } from 'react';
import { X } from 'react-feather';
import Modal, { ModalHandle } from './Modal';
import { Button, Field, Select, TextArea, TextInput, fieldAria } from './ui';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useResponsive } from '../hooks/useResponsive';
import {
  coachReportsAPI,
  CoachReportKind,
  CoachReportPosition,
  CoachReportRequest,
} from '../services/api';
import { schoolsAPI } from '../services/schoolsAPI';
import { font } from '../styles/tokens';

export interface ReportCoach {
  production_id: number;
  coach_name?: string | null;
  position?: string | null;
  title?: string | null;
  email?: string | null;
  phone?: string | null;
}

export interface CoachReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  schoolId: number;
  schoolName?: string | null;
  /** The active athlete. Guardians report for this athlete. */
  athleteId?: string;
  /** The coach being reported. Leave out to let the family pick. */
  coach?: ReportCoach | null;
  /** Coaches to pick from when no `coach` is given. */
  coaches?: ReportCoach[];
  /** Load the school's staff to pick from (the school card has no staff list). */
  loadCoaches?: boolean;
  initialKind?: CoachReportKind;
  /** True only when this viewer may see the coach's email and phone. */
  contactVisible?: boolean;
}

const KINDS: Array<{ value: CoachReportKind; label: string }> = [
  { value: 'departed', label: 'No longer at this school' },
  { value: 'contact', label: 'Wrong email or phone' },
  { value: 'title', label: 'Wrong title' },
  { value: 'missing', label: 'A coach is missing' },
];

const POSITIONS: CoachReportPosition[] = ['Head Coach', 'Assistant Coach'];
const NOTE_MAX = 500;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const DAILY_LIMIT_MESSAGE = "You've reached today's report limit. Try again tomorrow.";
const GENERIC_ERROR = 'Could not send that report. Try again.';

type FieldKey = 'coach' | 'email' | 'phone' | 'position' | 'title' | 'coach_name' | 'note';
type Errors = Partial<Record<FieldKey, string>>;

const isHead = (c: ReportCoach) =>
  /head/i.test(c.position || '') || /head/i.test(c.title || '');

export function coachLabel(c: ReportCoach): string {
  const name = (c.coach_name || '').trim();
  const pos = (c.position || '').trim();
  const title = (c.title || '').trim();
  const parts = [name || pos || 'Coach'];
  if (pos && pos !== parts[0]) parts.push(pos);
  if (title && title !== pos && title !== name) parts.push(title);
  return parts.join(', ');
}

/**
 * Labels for the coach picker. Locked coaches share a stand-in name such as
 * "Assistant Coach", so repeated labels get a number in list order:
 * "Assistant Coach 1", "Assistant Coach 2".
 */
export function coachLabels(list: ReportCoach[]): string[] {
  const base = list.map(coachLabel);
  const total = new Map<string, number>();
  base.forEach((l) => total.set(l, (total.get(l) || 0) + 1));
  const seen = new Map<string, number>();
  return base.map((l) => {
    if ((total.get(l) || 0) < 2) return l;
    const n = (seen.get(l) || 0) + 1;
    seen.set(l, n);
    return `${l} ${n}`;
  });
}

const phoneDigits = (v: string) => v.replace(/\D/g, '').length;

const CoachReportModal: React.FC<CoachReportModalProps> = ({
  isOpen,
  onClose,
  schoolId,
  schoolName,
  athleteId,
  coach = null,
  coaches,
  loadCoaches = false,
  initialKind,
  contactVisible = false,
}) => {
  const { sessionToken } = useAuth();
  const toast = useToast();
  const { isMobile } = useResponsive();
  const modalRef = useRef<ModalHandle>(null);
  const coachKey = coach ? String(coach.production_id) : '';

  const [kind, setKind] = useState<CoachReportKind | null>(initialKind || null);
  const [coachId, setCoachId] = useState<string>(coachKey);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [position, setPosition] = useState('');
  const [title, setTitle] = useState('');
  const [coachName, setCoachName] = useState('');
  const [note, setNote] = useState('');
  const [errors, setErrors] = useState<Errors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  const [staff, setStaff] = useState<ReportCoach[]>([]);
  const [loadedName, setLoadedName] = useState<string | null>(null);
  const [staffState, setStaffState] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');

  // Fresh form each time it opens.
  useEffect(() => {
    if (!isOpen) return;
    setKind(initialKind || null);
    setCoachId(coachKey);
    setEmail('');
    setPhone('');
    setPosition('');
    setTitle('');
    setCoachName('');
    setNote('');
    setErrors({});
    setFormError(null);
    setSending(false);
  }, [isOpen, coachKey, initialKind]);

  // The school card has no staff list, so load it here.
  useEffect(() => {
    if (!isOpen || coachKey || !loadCoaches || !sessionToken) return;
    let cancelled = false;
    setStaffState('loading');
    schoolsAPI
      .getComprehensiveSchool(sessionToken, schoolId, athleteId || undefined)
      .then((res) => {
        if (cancelled) return;
        const list = (res?.school?.coaching_staff || []).filter(
          (c: any) => c && c.production_id != null
        ) as ReportCoach[];
        const sorted = [...list].sort((a, b) => Number(isHead(b)) - Number(isHead(a)));
        setStaff(sorted);
        setLoadedName(res?.school?.name || null);
        setStaffState('ready');
      })
      .catch(() => {
        if (!cancelled) setStaffState('error');
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, coachKey, loadCoaches, sessionToken, schoolId, athleteId]);

  const pickList: ReportCoach[] = coach ? [coach] : loadCoaches ? staff : (coaches || []);
  const selectedCoach = pickList.find((c) => String(c.production_id) === coachId) || null;
  const firstPickId = pickList[0] ? String(pickList[0].production_id) : '';
  // Start the picker on the first coach (the head coach, when sorted).
  useEffect(() => {
    if (!coachId && firstPickId) setCoachId(firstPickId);
  }, [coachId, firstPickId]);
  const displaySchool = (schoolName && schoolName.trim()) || loadedName || 'this school';
  const staffFailed = !coach && loadCoaches && staffState === 'error';
  // Nothing to pick from: only "A coach is missing" makes sense.
  const noCoaches = (!!coach || !loadCoaches || staffState === 'ready') && pickList.length === 0;
  const pickLabels = coachLabels(pickList);

  const isDirty =
    !!(email.trim() || phone.trim() || title.trim() || coachName.trim() || note.trim() || position);

  const clearError = (key: FieldKey) => {
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
    setFormError(null);
  };

  const chooseKind = (k: CoachReportKind) => {
    setKind(k);
    setErrors({});
    setFormError(null);
  };

  const validate = (): Errors => {
    const e: Errors = {};
    const em = email.trim();
    const ph = phone.trim();
    if (kind !== 'missing' && !selectedCoach) e.coach = 'Pick a coach.';
    if (em && !EMAIL_RE.test(em)) e.email = 'Enter a valid email.';
    if (ph && phoneDigits(ph) < 10) e.phone = 'Enter a phone number with the area code.';
    if (kind === 'contact' && !em && !ph && !e.email && !e.phone) {
      e.email = 'Add the right email or phone number.';
    }
    if (kind === 'title' && !position && !title.trim()) {
      e.position = 'Pick the right position or add the right title.';
    }
    if (kind === 'missing') {
      const name = coachName.trim();
      if (name.length < 2 || !/[a-z]/i.test(name)) e.coach_name = "Add the coach's name.";
      if (!position) e.position = 'Pick Head Coach or Assistant Coach.';
    }
    if (note.length > NOTE_MAX) e.note = `Keep the note under ${NOTE_MAX} characters.`;
    return e;
  };

  const buildRequest = (): CoachReportRequest => {
    const req: CoachReportRequest = { kind: kind as CoachReportKind, school_id: schoolId };
    if (athleteId) req.athlete_id = athleteId;
    if (note.trim()) req.note = note.trim();
    if (kind !== 'missing' && selectedCoach) req.production_id = selectedCoach.production_id;
    if (kind === 'contact' || kind === 'missing') {
      if (email.trim()) req.email = email.trim();
      if (phone.trim()) req.phone = phone.trim();
    }
    if (kind === 'title' || kind === 'missing') {
      if (position) req.position = position as CoachReportPosition;
      if (title.trim()) req.title = title.trim();
    }
    if (kind === 'missing') req.coach_name = coachName.trim();
    return req;
  };

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (sending) return;
    if (!kind) {
      setFormError('Pick what is wrong.');
      return;
    }
    const found = validate();
    if (Object.values(found).some(Boolean)) {
      setErrors(found);
      return;
    }
    if (!sessionToken) {
      setFormError('Sign in again to send a report.');
      return;
    }
    setSending(true);
    setFormError(null);
    try {
      const result = await coachReportsAPI.send(sessionToken, buildRequest());
      const message = result?.message || 'Thanks. Our team will check it.';
      if (result?.outcome === 'no_change' || result?.outcome === 'already_listed') {
        toast.info(message);
      } else {
        toast.success(message);
      }
      setSending(false);
      onClose();
    } catch (err: any) {
      setSending(false);
      const code = err?.code as string | undefined;
      const msg = typeof err?.message === 'string' && err?.status ? err.message : '';
      if (err?.status === 429 || code === 'DAILY_LIMIT') {
        setFormError(msg || DAILY_LIMIT_MESSAGE);
        return;
      }
      const fieldFor: Record<string, FieldKey> = {
        INVALID_EMAIL: 'email',
        INVALID_PHONE: 'phone',
        INVALID_NAME: 'coach_name',
        INVALID_POSITION: 'position',
        INVALID_COACH: 'coach',
      };
      if (code && fieldFor[code]) {
        setErrors({ [fieldFor[code]]: msg || 'Check this and try again.' });
        return;
      }
      if (code === 'COACH_NOT_FOUND') {
        setFormError('That coach is no longer listed for this school.');
        return;
      }
      setFormError(msg && err.status !== 500 ? msg : GENERIC_ERROR);
    }
  };

  if (!isOpen) return null;

  const currentEmail = contactVisible && selectedCoach?.email ? selectedCoach.email : null;
  const currentPhone = contactVisible && selectedCoach?.phone ? selectedCoach.phone : null;
  const listedNow = selectedCoach
    ? [selectedCoach.position, selectedCoach.title && selectedCoach.title !== selectedCoach.position ? selectedCoach.title : null]
        .filter(Boolean)
        .join(', ')
    : '';

  const subtitle = coach && kind !== 'missing' ? `${coachLabel(coach)} at ${displaySchool}` : displaySchool;
  const showCoachPicker = !coach && kind !== null && kind !== 'missing';

  const optionStyle = (selected: boolean, disabled: boolean): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '12px 14px',
    borderRadius: 10,
    border: `1.5px solid ${selected ? 'var(--aps-action)' : 'var(--aps-border-2)'}`,
    background: selected ? 'var(--aps-action-tint)' : 'var(--aps-surface)',
    color: disabled ? 'var(--aps-text-muted)' : 'var(--aps-ink)',
    fontFamily: font.sans,
    fontSize: font.size.label,
    fontWeight: font.weight.semibold,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.6 : 1,
  });

  return (
    <Modal
      ref={modalRef}
      isOpen={isOpen}
      onClose={onClose}
      labelledById="coach-report-heading"
      isDirty={isDirty}
      disableClose={sending}
      zIndex={1002}
      discardTitle="Discard this report?"
      discardMessage="What you typed will be lost."
      contentStyle={{
        maxWidth: isMobile ? 'calc(100vw - 32px)' : '520px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: font.sans,
        background: 'var(--aps-surface)',
      }}
    >
      <div
        style={{
          padding: isMobile ? '16px' : '20px 24px',
          borderBottom: '1px solid var(--aps-border)',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
          flexShrink: 0,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <h2
            id="coach-report-heading"
            style={{ margin: 0, fontSize: 18, fontWeight: font.weight.bold, color: 'var(--aps-ink)' }}
          >
            Report a change
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: font.size.labelSm, color: 'var(--aps-text-2)' }}>
            {subtitle}
          </p>
        </div>
        <button
          type="button"
          onClick={() => modalRef.current?.requestClose()}
          disabled={sending}
          aria-label="Close"
          style={{
            background: 'transparent',
            border: 'none',
            padding: 4,
            cursor: sending ? 'not-allowed' : 'pointer',
            display: 'flex',
            color: 'var(--aps-text-2)',
          }}
        >
          <X size={20} aria-hidden="true" />
        </button>
      </div>

      <form
        onSubmit={handleSubmit}
        noValidate
        style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
      >
        <div
          style={{
            padding: isMobile ? '16px' : '20px 24px',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 18,
          }}
        >
          <p style={{ margin: 0, fontSize: font.size.label, color: 'var(--aps-text-2)', lineHeight: 1.5 }}>
            Our team checks every report before anything changes.
          </p>

          <fieldset style={{ border: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <legend
              style={{
                padding: 0,
                marginBottom: 8,
                fontSize: font.size.label,
                fontWeight: font.weight.semibold,
                color: 'var(--aps-ink)',
              }}
            >
              What is wrong?
            </legend>
            {KINDS.map((k) => {
              const disabled = k.value !== 'missing' && noCoaches;
              const selected = kind === k.value;
              return (
                <label key={k.value} style={optionStyle(selected, disabled)}>
                  <input
                    type="radio"
                    name="coach-report-kind"
                    value={k.value}
                    checked={selected}
                    disabled={disabled || sending}
                    onChange={() => chooseKind(k.value)}
                    style={{ accentColor: 'var(--aps-action)', margin: 0 }}
                  />
                  {k.label}
                </label>
              );
            })}
            {noCoaches && !coach && (
              <span style={{ fontSize: font.size.labelSm, color: 'var(--aps-text-2)' }}>
                No coaches are listed for this school yet.
              </span>
            )}
          </fieldset>

          {staffFailed && (
            <p role="alert" style={{ margin: 0, fontSize: font.size.label, color: 'var(--aps-danger)' }}>
              Could not load the coaches. Close this and try again.
            </p>
          )}

          {showCoachPicker && (
            loadCoaches && staffState === 'loading' ? (
              <p role="status" style={{ margin: 0, fontSize: font.size.label, color: 'var(--aps-text-2)' }}>
                Loading coaches...
              </p>
            ) : pickList.length > 0 ? (
              <Field id="coach-report-coach" label="Which coach?" error={errors.coach || null}>
                <Select
                  {...fieldAria('coach-report-coach', { error: errors.coach || null })}
                  value={coachId}
                  invalid={!!errors.coach}
                  disabled={sending}
                  onChange={(e) => {
                    setCoachId(e.target.value);
                    clearError('coach');
                  }}
                >
                  {pickList.map((c, i) => (
                    <option key={c.production_id} value={String(c.production_id)}>
                      {pickLabels[i]}
                    </option>
                  ))}
                </Select>
              </Field>
            ) : null
          )}
          {errors.coach && !(showCoachPicker && pickList.length > 0) && (
            <p role="alert" style={{ margin: 0, fontSize: font.size.labelSm, color: 'var(--aps-danger)' }}>
              {errors.coach}
            </p>
          )}

          {kind === 'contact' && (
            <>
              <p style={{ margin: 0, fontSize: font.size.labelSm, color: 'var(--aps-text-2)' }}>
                Add the right email, phone or both.
              </p>
              <Field
                id="coach-report-email"
                label="Correct email"
                hint={currentEmail ? `On file now: ${currentEmail}` : undefined}
                error={errors.email || null}
              >
                <TextInput
                  {...fieldAria('coach-report-email', { hint: !!currentEmail, error: errors.email || null })}
                  type="email"
                  autoComplete="off"
                  value={email}
                  invalid={!!errors.email}
                  disabled={sending}
                  maxLength={255}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    clearError('email');
                  }}
                />
              </Field>
              <Field
                id="coach-report-phone"
                label="Correct phone"
                hint={currentPhone ? `On file now: ${currentPhone}` : undefined}
                error={errors.phone || null}
              >
                <TextInput
                  {...fieldAria('coach-report-phone', { hint: !!currentPhone, error: errors.phone || null })}
                  type="tel"
                  autoComplete="off"
                  value={phone}
                  invalid={!!errors.phone}
                  disabled={sending}
                  maxLength={50}
                  onChange={(e) => {
                    setPhone(e.target.value);
                    clearError('phone');
                  }}
                />
              </Field>
            </>
          )}

          {kind === 'title' && (
            <>
              <Field
                id="coach-report-position"
                label="Position"
                hint={listedNow ? `Listed now: ${listedNow}` : undefined}
                error={errors.position || null}
              >
                <Select
                  {...fieldAria('coach-report-position', { hint: !!listedNow, error: errors.position || null })}
                  value={position}
                  invalid={!!errors.position}
                  disabled={sending}
                  onChange={(e) => {
                    setPosition(e.target.value);
                    clearError('position');
                  }}
                >
                  <option value="">Keep as listed</option>
                  {POSITIONS.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </Select>
              </Field>
              <Field id="coach-report-title" label="Correct title" hint="For example, Pitching Coach." error={errors.title || null}>
                <TextInput
                  {...fieldAria('coach-report-title', { hint: true, error: errors.title || null })}
                  value={title}
                  disabled={sending}
                  maxLength={150}
                  onChange={(e) => {
                    setTitle(e.target.value);
                    clearError('title');
                    clearError('position');
                  }}
                />
              </Field>
            </>
          )}

          {kind === 'missing' && (
            <>
              <Field id="coach-report-name" label="Coach name" required error={errors.coach_name || null}>
                <TextInput
                  {...fieldAria('coach-report-name', { error: errors.coach_name || null })}
                  value={coachName}
                  autoComplete="off"
                  invalid={!!errors.coach_name}
                  disabled={sending}
                  maxLength={255}
                  onChange={(e) => {
                    setCoachName(e.target.value);
                    clearError('coach_name');
                  }}
                />
              </Field>
              <Field id="coach-report-position" label="Position" required error={errors.position || null}>
                <Select
                  {...fieldAria('coach-report-position', { error: errors.position || null })}
                  value={position}
                  invalid={!!errors.position}
                  disabled={sending}
                  onChange={(e) => {
                    setPosition(e.target.value);
                    clearError('position');
                  }}
                >
                  <option value="">Pick one</option>
                  {POSITIONS.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </Select>
              </Field>
              <Field id="coach-report-title" label="Title" hint="For example, Pitching Coach." error={errors.title || null}>
                <TextInput
                  {...fieldAria('coach-report-title', { hint: true, error: errors.title || null })}
                  value={title}
                  disabled={sending}
                  maxLength={150}
                  onChange={(e) => {
                    setTitle(e.target.value);
                    clearError('title');
                  }}
                />
              </Field>
              <Field id="coach-report-email" label="Email" error={errors.email || null}>
                <TextInput
                  {...fieldAria('coach-report-email', { error: errors.email || null })}
                  type="email"
                  autoComplete="off"
                  value={email}
                  invalid={!!errors.email}
                  disabled={sending}
                  maxLength={255}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    clearError('email');
                  }}
                />
              </Field>
              <Field id="coach-report-phone" label="Phone" error={errors.phone || null}>
                <TextInput
                  {...fieldAria('coach-report-phone', { error: errors.phone || null })}
                  type="tel"
                  autoComplete="off"
                  value={phone}
                  invalid={!!errors.phone}
                  disabled={sending}
                  maxLength={50}
                  onChange={(e) => {
                    setPhone(e.target.value);
                    clearError('phone');
                  }}
                />
              </Field>
            </>
          )}

          {kind !== null && (
            <Field
              id="coach-report-note"
              label="Note"
              hint="Optional. A link to the staff page helps."
              error={errors.note || null}
              accessory={
                <span style={{ fontSize: font.size.caption, color: 'var(--aps-text-muted)' }}>
                  {note.length}/{NOTE_MAX}
                </span>
              }
            >
              <TextArea
                {...fieldAria('coach-report-note', { hint: true, error: errors.note || null })}
                rows={3}
                value={note}
                disabled={sending}
                maxLength={NOTE_MAX}
                onChange={(e) => {
                  setNote(e.target.value);
                  clearError('note');
                }}
              />
            </Field>
          )}

          {formError && (
            <div
              role="alert"
              style={{
                padding: '10px 14px',
                borderRadius: 10,
                background: 'var(--aps-danger-tint)',
                color: 'var(--aps-danger)',
                fontSize: font.size.labelSm,
                fontWeight: font.weight.semibold,
                lineHeight: 1.4,
              }}
            >
              {formError}
            </div>
          )}
        </div>

        <div
          style={{
            padding: isMobile ? '12px 16px' : '16px 24px',
            borderTop: '1px solid var(--aps-border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 10,
            flexShrink: 0,
          }}
        >
          <Button type="button" variant="secondary" onClick={() => modalRef.current?.requestClose()} disabled={sending}>
            Cancel
          </Button>
          <Button type="submit" loading={sending} disabled={kind === null}>
            Send
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CoachReportModal;
